/**
 * Query-fed Item Pools — the pure core (Large Sets PRD §4.3, migration 723).
 *
 * The Single List Invariant: a pool is never live-smart. This module only
 * COMPUTES — what a feed matches, how that differs from the pool, what has
 * arrived since the feed was severed at lock, and what locking now would
 * leave behind. Every write goes through poolFeedService.ts, and every write
 * there is a person accepting a diff this module produced.
 *
 * Vocabulary: a feed rule is evaluated off a raw Item with no cross-store
 * join, exactly like a Smart Collection rule (smartCollectionFields.ts), so
 * the same catalog and resolver are used — one rule language across both
 * query-derived sets.
 */

import { evaluateNode } from './pql/evaluate';
import type { QueryNode } from './pql/types';
import { getSmartCollectionFieldValue } from './collections/smartCollectionFields';
import type { Item, ItemPool } from '../types/prioritization/lifecycle';

export type ArrivalDisposition = 'held' | 'carried' | 'amended' | 'dismissed';

export interface ArrivalRecord {
  itemId: string;
  disposition: ArrivalDisposition;
  matchedAt: string;
  carriedToPoolId?: string | null;
}

export interface FeedDiff {
  /** Matches the feed now that are not in the pool. */
  adds: string[];
  /** Pool members the feed no longer matches (never removed without a person accepting it). */
  removals: string[];
  /** Pool members the feed still matches. */
  kept: string[];
}

export type FeedPhase = 'unfed' | 'open' | 'severed';

/** Where a pool sits in the feed lifecycle. */
export function feedPhase(pool: Pick<ItemPool, 'feedQueryId' | 'feedSeveredAt' | 'poolLockedAt'>): FeedPhase {
  if (!pool.feedQueryId) return 'unfed';
  if (pool.feedSeveredAt || pool.poolLockedAt) return 'severed';
  return 'open';
}

/** Item ids the rule matches. `undefined` rule = no feed = nothing. `null` rule = everything. */
export function evaluateFeedMatchIds(items: Item[], ruleAst: QueryNode | null | undefined): string[] {
  if (ruleAst === undefined) return [];
  return items
    .filter((item) => evaluateNode(ruleAst, (field) => getSmartCollectionFieldValue(item, field)))
    .map((item) => item.id);
}

/** The diff a "Refresh from feed" shows before writing anything. */
export function computeFeedDiff(memberIds: string[], matchedIds: string[]): FeedDiff {
  const members = new Set(memberIds);
  const matched = new Set(matchedIds);
  const adds: string[] = [];
  for (const id of matchedIds) if (!members.has(id)) adds.push(id);
  const removals: string[] = [];
  const kept: string[] = [];
  for (const id of memberIds) (matched.has(id) ? kept : removals).push(id);
  return { adds: dedupe(adds), removals: dedupe(removals), kept: dedupe(kept) };
}

/** Keeps a reason inside the column's practical width, as Add-to-pool does. */
const REASON_MAX = 500;

/**
 * `selection_reason` for a membership written by an accepted refresh (PRD §4.3).
 *
 * The rule TEXT is part of the reason, not just the query id: a feed rule can
 * be edited in place afterwards, and an id alone would then resolve to a rule
 * that is not the one this item matched. The text is the rule as it stood when
 * the item came in.
 */
export function feedSelectionReason(queryId: string, evaluatedAt: Date, queryText?: string): string {
  const stamp = `query:${queryId}@${evaluatedAt.toISOString()}`;
  const rule = queryText?.trim();
  return rule ? `${stamp} — ${rule}`.slice(0, REASON_MAX) : stamp;
}

export interface ArrivalBay {
  /** Matches since severance with no arrival row yet — the caller records these as 'held'. */
  newArrivals: string[];
  /** Held arrivals, oldest first. */
  held: ArrivalRecord[];
  /** Held arrivals the feed no longer matches (still held — a person decides). */
  staleHeld: string[];
  dismissed: ArrivalRecord[];
  carried: ArrivalRecord[];
  amended: ArrivalRecord[];
}

/**
 * The Arrival Bay for a severed pool: what matches now but is not in the
 * locked list, reconciled with what has already been recorded and disposed.
 * Membership is never touched here — an arrival is a sighting, not a seat.
 */
export function resolveArrivalBay(params: {
  matchedIds: string[];
  memberIds: string[];
  arrivals: ArrivalRecord[];
}): ArrivalBay {
  const members = new Set(params.memberIds);
  const matched = new Set(params.matchedIds);
  const known = new Map(params.arrivals.map((a) => [a.itemId, a]));

  const newArrivals = dedupe(params.matchedIds.filter((id) => !members.has(id) && !known.has(id)));
  const byDisposition = (d: ArrivalDisposition) =>
    params.arrivals
      .filter((a) => a.disposition === d && !members.has(a.itemId))
      .sort((a, b) => a.matchedAt.localeCompare(b.matchedAt));

  const held = byDisposition('held');
  return {
    newArrivals,
    held,
    staleHeld: held.filter((a) => !matched.has(a.itemId)).map((a) => a.itemId),
    dismissed: byDisposition('dismissed'),
    carried: byDisposition('carried'),
    // An amended arrival IS a member now; report it from the record, not the membership filter.
    amended: params.arrivals
      .filter((a) => a.disposition === 'amended')
      .sort((a, b) => a.matchedAt.localeCompare(b.matchedAt)),
  };
}

export interface LockCoverage {
  pendingAdds: number;
  pendingRemovals: number;
  /** Null when locking now leaves nothing behind. */
  warning: string | null;
}

/** The coverage check at lock: what a lock right now would freeze out. */
export function coverageAtLock(diff: FeedDiff): LockCoverage {
  const pendingAdds = diff.adds.length;
  const pendingRemovals = diff.removals.length;
  if (pendingAdds === 0 && pendingRemovals === 0) return { pendingAdds, pendingRemovals, warning: null };
  const parts: string[] = [];
  if (pendingAdds > 0) parts.push(`${pendingAdds} matching item${pendingAdds === 1 ? '' : 's'} not yet in the pool`);
  if (pendingRemovals > 0) parts.push(`${pendingRemovals} member${pendingRemovals === 1 ? '' : 's'} the feed no longer matches`);
  return {
    pendingAdds,
    pendingRemovals,
    warning: `Locking now severs the feed with ${parts.join(' and ')}. Refresh first, or lock and let them wait in the Arrival Bay.`,
  };
}

function dedupe(ids: string[]): string[] {
  return [...new Set(ids)];
}
